import type { Body, OrbitalElements } from './types';
import { positionAtTime, normalizeAngle } from './kepler';

/**
 * Pure analytic orbit geometry (AD-1), in the focus-centred SI frame (origin at
 * the central body's focus, +y up). This is the geometry the renderer reads so no
 * adapter recomputes orbital math (AD-3). Story 1.2 adds time evolution in
 * `kepler.ts`, layered on top of this same frame with no re-offset by c = a*e;
 * the time-zero position below delegates to that solver so the frame convention
 * is single-sourced and never drifts (AD-10).
 *
 * Convention: periapsis lies on the +x axis, so the ellipse centre sits at
 * (-c, 0) with c = a*e, and apoapsis lies on the -x axis.
 */

export interface OrbitEllipse {
  /** Ellipse centre x in world metres (focus at origin, so centreX = -a*e). */
  readonly centreX: number;
  /** Ellipse centre y in world metres. */
  readonly centreY: number;
  /** Semi-major axis in metres. */
  readonly semiMajor: number;
  /** Semi-minor axis in metres, b = a*sqrt(1 - e^2). */
  readonly semiMinor: number;
}

/**
 * The orbit ellipse of a body, focus-centred. Accepts `OrbitalElements` so the
 * live store element (`{ a, e }`) and a full `Body` feed the identical pure math
 * (single source of truth, Story 1.3). No consumer re-offsets by c = a*e.
 */
export function orbitEllipse(elements: OrbitalElements): OrbitEllipse {
  const c = elements.a * elements.e;
  const semiMinor = elements.a * Math.sqrt(1 - elements.e * elements.e);
  return {
    centreX: -c,
    centreY: 0,
    semiMajor: elements.a,
    semiMinor
  };
}

/** Apoapsis distance from the focus, a*(1 + e). */
export function apoapsisRadius(elements: OrbitalElements): number {
  return elements.a * (1 + elements.e);
}

/** Periapsis distance from the focus, a*(1 - e). */
export function periapsisRadius(elements: OrbitalElements): number {
  return elements.a * (1 - elements.e);
}

/**
 * Vis-viva orbital speed in SI metres per second: v = sqrt(mu*(2/r - 1/a)), where
 * `centralMu` is the CURRENT central body's gravitational parameter (never the
 * orbiting body's, PRD 5.3), `r` is the current focus distance, and `a` the
 * semi-major axis. At e = 0, r = a at every phase, so v = sqrt(mu/a) is constant.
 * Pure and SI (AD-5): no km/s literal here; unit labelling happens in `ui`.
 */
export function orbitalSpeed(centralMu: number, r: number, a: number): number {
  return Math.sqrt(centralMu * (2 / r - 1 / a));
}

/**
 * Time-zero position of an orbiting body in the focus-centred frame. At mean
 * anomaly zero the body is at periapsis, on the +x axis at distance a*(1 - e).
 * Delegates to the Kepler solver at t = 0 so the perifocal frame is stated once
 * (in `kepler.ts`) and the static and animated paths cannot drift (AD-10). The
 * central mu is irrelevant at t = 0, so any value gives periapsis; the solver
 * short-circuits t = 0 without reading it.
 */
export function positionAtTimeZero(body: Body): { x: number; y: number } {
  return positionAtTime(body, body.mu, 0);
}

/**
 * Pure world-point -> mean-anomaly inverse (AD-1, AD-5). Maps a focus-centred SI
 * world point (metres) to the mean anomaly of the corresponding point on the
 * body's ellipse. Stays pure and SI: no DOM, no projection, no render dependency;
 * the screen -> world step is the render projection's job (AD-10), and this
 * consumes only the resulting world point.
 *
 * Convention (identical to `orbitEllipse`): focus at origin, ellipse centre at
 * (-c, 0) with c = a*e, periapsis on +x, semi-minor b = a*sqrt(1 - e^2).
 *   ex = wx - centreX,  ey = wy
 *   E  = atan2(ey / b, ex / a)          (eccentric anomaly of the radial projection)
 *   M  = normalizeAngle(E - e*sin(E))   (forward Kepler's equation)
 *
 * This radial (eccentric-anomaly `atan2`) projection is the DELIBERATE meaning of
 * "nearest point on the ellipse" for the live drag: the standard radial
 * projection, not the Euclidean quartic minimum, and visually identical for a
 * pointer at or near the ellipse. Because the phase-only drag model keeps the body
 * ON its ellipse at all times, this projection IS the snap; the body is never
 * rendered off-ellipse and animated back.
 */
export function meanAnomalyAtWorldPoint(
  elements: OrbitalElements,
  wx: number,
  wy: number
): number {
  const { a, e } = elements;
  const semiMinor = a * Math.sqrt(1 - e * e);
  const centreX = -a * e;
  const ex = wx - centreX;
  const ey = wy;
  const eccentricAnomaly = Math.atan2(ey / semiMinor, ex / a);
  return normalizeAngle(eccentricAnomaly - e * Math.sin(eccentricAnomaly));
}

/**
 * Largest apoapsis among the given orbiting bodies (used to frame "fit all").
 * Accepts `OrbitalElements` so store-derived live elements can drive the framing;
 * as e grows the live apoapsis grows and auto-fit reframes (AD-10).
 */
export function maxApoapsis(bodies: readonly OrbitalElements[]): number {
  let max = 0;
  for (const body of bodies) {
    if (body.a <= 0) continue; // the central Sun does not orbit
    const r = apoapsisRadius(body);
    if (r > max) max = r;
  }
  return max;
}
